import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import Papa from 'papaparse';
import { FC } from 'react';

import { ChartSpec, TableSpec, ValueFormat } from '../../interfaces';
import { AnalystChart } from '../../interfaces/models';
import InsightChart from '../InsightChart/InsightChart';
import InsightTable from '../InsightTable/InsightTable';
import { humanize } from '../Models/common';
import { saveBlob } from '../../services/models';

const FORMAT: Record<AnalystChart['format'], ValueFormat> = { currency: 'inr', number: 'count', percent: 'percent' };

// The analyst's charts carry labels and series built server-side from tool results; this adapts them to the
// page's own chart and table components so they look like every other chart here.
export const toRows = (chart: AnalystChart) =>
  chart.labels.map((label, i) => ({
    [chart.x]: label,
    ...Object.fromEntries(chart.series.map((s) => [s.name, s.values[i] ?? null])),
  }));

// The numbers behind a chart or table, as a CSV with readable headings.
export const downloadChartCsv = (chart: AnalystChart) => {
  const csv = Papa.unparse({
    fields: [humanize(chart.x), ...chart.series.map((s) => humanize(s.name))],
    data: chart.labels.map((label, i) => [label, ...chart.series.map((s) => s.values[i] ?? '')]),
  });
  saveBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `${chart.title.replace(/[^\w-]+/g, '-').replace(/^-|-$/g, '') || 'gargi-data'}.csv`);
};

const Title: FC<{ chart: AnalystChart }> = ({ chart }) => (
  <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 1 }}>
    <Typography variant='subtitle2' fontWeight={700}>{chart.title}</Typography>
    <Tooltip title='Download as CSV'>
      <IconButton size='small' onClick={() => downloadChartCsv(chart)} aria-label={`Download ${chart.title} as CSV`}>
        <FileDownloadOutlinedIcon fontSize='small' />
      </IconButton>
    </Tooltip>
  </Stack>
);

const AnalystChartView: FC<{ chart: AnalystChart }> = ({ chart }) => {
  const format = FORMAT[chart.format] ?? 'count';
  const rows = toRows(chart);
  if (chart.kind === 'table') {
    const spec: TableSpec = {
      columns: [{ key: chart.x, label: humanize(chart.x) },
        ...chart.series.map((s) => ({ key: s.name, label: humanize(s.name), format, align: 'right' as const }))],
      rows,
    };
    return (
      <Box>
        <Title chart={chart} />
        <InsightTable spec={spec} />
      </Box>
    );
  }
  const type: ChartSpec['type'] = chart.kind === 'line' ? 'line' : chart.kind === 'bar' || chart.kind === 'stacked_bar' ? 'bar' : 'horizontalBar';
  const spec: ChartSpec = {
    type,
    x: { key: chart.x, label: humanize(chart.x) },
    series: chart.series.map((s) => ({ key: s.name, label: humanize(s.name), format })),
    rows,
  };
  return (
    <Box>
      <Title chart={chart} />
      <InsightChart spec={spec} height={type === 'horizontalBar' ? Math.max(200, rows.length * 28) : 240} />
    </Box>
  );
};

export default AnalystChartView;
